// src/components/StepEmailVerification.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import OtpInput from "react-otp-input";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
} from "@mui/material";
import api, { ensureCsrfToken } from "../utils/api";

// 🔹 Styled OTP input field with MUI theme
const OtpTextField = styled("input")(({ theme }) => ({
  width: "3rem",
  height: "3.5rem",
  margin: "0.5rem",
  fontSize: "1.5rem",
  textAlign: "center",
  borderRadius: "0.75rem",
  border: `1.5px solid ${theme.palette.info.main}`,
  background: "rgba(255,255,255,0.05)",
  color: "#fff",
  outline: "none",
  "&:focus": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 8px ${theme.palette.primary.light}`,
  },
}));

const StepEmailVerification = ({ user, onVerified }) => {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [message, setMessage] = useState(null);
  const [verifying, setVerifying] = useState(false); // ✅ guard flag

  // countdown
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const sendCode = async () => {
    try {
      setLoading(true);
      await ensureCsrfToken();
      await api.post("/api/email/send-code", { email: user.email });
      setSent(true);
      setTimer(60);
      setMessage({ type: "success", text: "Code sent to your email." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to send code.",
      });
    } finally {
      setLoading(false);
    }
  };

  // StepEmailVerification.jsx
const verifyCode = async (value) => {
  setCode(value);
  if (value.length === 6 && !verifying) {
    setVerifying(true);
    try {
      setLoading(true);
      await ensureCsrfToken();
      await api.post("/api/email/verify-code", {
        email: user.email,
        code: value,
      });

      setMessage({ type: "success", text: "Email verified successfully!" });

      // ✅ Pass verified email to parent
      setTimeout(() => onVerified(user.email), 800);

    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Invalid code.",
      });
      setCode("");
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  }
};


  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Typography color="text.secondary">Loading user info...</Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="70vh"
      sx={{
        background: "linear-gradient(to bottom right, #0f172a, #000)",
        position: "relative",
      }}
    >
      {/* Background glow */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(circle at top left,#0ea5e9,#0f172a)",
          opacity: 0.5,
          filter: "blur(60px)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 4,
            maxWidth: 400,
            borderRadius: 4,
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(34,211,238,0.3)",
            backdropFilter: "blur(12px)",
            position: "relative",
            zIndex: 10,
          }}
        >
          <Typography
            variant="h5"
            align="center"
            fontWeight="bold"
            color="info.light"
            gutterBottom
          >
            Verify Your Email
          </Typography>

          <Box textAlign="center" mb={3}>
            <Typography variant="body2" color="text.secondary">
              Verification code will be sent to:
            </Typography>
            <Typography variant="body1" color="info.main" fontFamily="monospace">
              {user?.email || "Loading..."}
            </Typography>
          </Box>

          {!sent ? (
            <Button
              fullWidth
              variant="contained"
              color="info"
              onClick={sendCode}
              disabled={loading}
              sx={{ py: 1.5, fontWeight: "bold", borderRadius: 3 }}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Send Code
            </Button>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
              <OtpInput
                value={code}
                onChange={verifyCode}
                numInputs={6}
                isInputNum
                shouldAutoFocus
                renderInput={(props) => (
                  <OtpTextField {...props} disabled={verifying} /> // ✅ disable while verifying
                )}
              />

              {timer > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Resend available in {timer}s
                </Typography>
              ) : (
                <Button
                  onClick={sendCode}
                  disabled={loading}
                  color="info"
                  variant="text"
                >
                  Resend Code
                </Button>
              )}
            </Box>
          )}

          {message && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Typography
                align="center"
                mt={3}
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: message.type === "success" ? "success.main" : "error.main",
                }}
              >
                {message.text}
              </Typography>
            </motion.div>
          )}
        </Paper>
      </motion.div>
    </Box>
  );
};

export default StepEmailVerification;